import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from 'rehype-katex';
import type { ReactNode } from 'react';
import { Text } from '@/components/Typography';
import { splitMarkdownParts } from '@/utils/markdown';
import { CodeBlock } from './CodeBlock';
import { MermaidDiagram } from './MermaidDiagram';

type MarkdownComponentProps<T extends HTMLElement> = React.HTMLAttributes<T> & {
  node?: unknown;
  children?: ReactNode;
};

type ReactMarkdownComponents = React.ComponentProps<typeof ReactMarkdown>['components'];

type MarkdownProps = {
  children: string;
  className?: string;
};

export const Markdown: React.FC<MarkdownProps> = ({ children, className }) => {
  const parsedContent = useMemo(() => {
    return splitMarkdownParts(children);
  }, [children]);

  const markdownComponents: ReactMarkdownComponents = {
    h1: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLHeadingElement>) => (
      <Text
        variant="text-lg-bold"
        className="mb-3 text-grey-main dark:text-white"
        as="h3"
        {...props}
      >
        {nodeChildren}
      </Text>
    ),
    h2: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLHeadingElement>) => (
      <Text
        variant="text-lg-bold"
        className="mb-3 text-grey-main dark:text-white"
        as="h3"
        {...props}
      >
        {nodeChildren}
      </Text>
    ),
    h3: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLHeadingElement>) => (
      <Text
        variant="text-lg-bold"
        className="mb-3 text-grey-main dark:text-white"
        as="h3"
        {...props}
      >
        {nodeChildren}
      </Text>
    ),
    p: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLParagraphElement>) => (
      <Text
        variant="text-md-regular"
        className="mb-3 text-grey-main dark:text-grey-light"
        as="p"
        {...props}
      >
        {nodeChildren}
      </Text>
    ),
    strong: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLElement>) => (
      <strong className="font-semibold text-grey-main dark:text-white" {...props}>
        {nodeChildren}
      </strong>
    ),
    em: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLElement>) => (
      <em className="italic" {...props}>
        {nodeChildren}
      </em>
    ),
    ul: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLUListElement>) => (
      <ul
        className="list-disc pl-5 mb-3 space-y-1.5 marker:text-grey-hover dark:marker:text-grey-hover-dark"
        {...props}
      >
        {nodeChildren}
      </ul>
    ),
    ol: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLOListElement>) => (
      <ol
        className="list-decimal pl-5 mb-3 space-y-1.5 marker:text-grey-hover dark:marker:text-grey-hover-dark"
        {...props}
      >
        {nodeChildren}
      </ol>
    ),
    li: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLLIElement>) => (
      <li
        className="mb-0.5 text-[14px] leading-[17px] text-grey-main dark:text-grey-light pl-1"
        {...props}
      >
        {nodeChildren}
      </li>
    ),
    a: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLAnchorElement>) => (
      <a
        className="text-positive-primary dark:text-positive-primary-dark underline hover:text-positive-hover dark:hover:text-positive-hover-dark hover:no-underline transition-colors"
        target="_blank"
        rel="noreferrer"
        {...props}
      >
        {nodeChildren}
      </a>
    ),
    code: ({
      children: nodeChildren,
      className,
      ...props
    }: MarkdownComponentProps<HTMLElement>) => {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';

      // Render Mermaid diagrams
      if (language === 'mermaid') {
        return <MermaidDiagram chart={String(nodeChildren).replace(/\n$/, '')} />;
      }

      // Render code blocks with syntax highlighting
      if (language) {
        return (
          <CodeBlock language={language} className={className}>
            {String(nodeChildren).replace(/\n$/, '')}
          </CodeBlock>
        );
      }

      // Inline code
      return (
        <code className="rounded bg-grey-light px-1 py-0.5 text-sm dark:bg-grey-black" {...props}>
          {nodeChildren}
        </code>
      );
    },
    pre: ({ children: nodeChildren }: MarkdownComponentProps<HTMLPreElement>) => (
      // CodeBlock handles the pre wrapper, so just return children
      <>{nodeChildren}</>
    ),
    hr: ({ ...props }: MarkdownComponentProps<HTMLHRElement>) => (
      <hr className="my-4 border-grey-plain dark:border-grey-plain-dark" {...props} />
    ),
    blockquote: ({
      children: nodeChildren,
      ...props
    }: MarkdownComponentProps<HTMLQuoteElement>) => (
      <blockquote
        className="border-l-4 border-grey-hover dark:border-grey-hover-dark pl-4 py-2 my-3 bg-grey-light dark:bg-grey-light-dark rounded-r-md italic text-grey-main dark:text-grey-main-dark"
        {...props}
      >
        {nodeChildren}
      </blockquote>
    ),
    br: () => <br />,
    // Tables
    table: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLTableElement>) => (
      <div className="relative my-4">
        <div
          className="overflow-x-auto rounded-lg border border-grey-plain dark:border-grey-plain-dark bg-white dark:bg-grey-black"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <table className="w-full border-collapse table-auto" {...props}>
            {nodeChildren}
          </table>
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 md:hidden rounded-r-lg bg-gradient-to-r from-transparent to-white dark:to-grey-black" />
      </div>
    ),
    thead: ({
      children: nodeChildren,
      ...props
    }: MarkdownComponentProps<HTMLTableSectionElement>) => <thead {...props}>{nodeChildren}</thead>,
    th: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLTableCellElement>) => (
      <th
        className="border border-grey-plain dark:border-grey-plain-dark px-3 py-2 text-left font-semibold text-grey-main dark:text-white bg-grey-light dark:bg-grey-hover text-sm"
        {...props}
      >
        {nodeChildren}
      </th>
    ),
    td: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLTableCellElement>) => (
      <td
        className="border border-grey-plain dark:border-grey-plain-dark px-3 py-2 text-grey-main dark:text-grey-light text-sm align-top"
        {...props}
      >
        {nodeChildren}
      </td>
    ),
    tbody: ({
      children: nodeChildren,
      ...props
    }: MarkdownComponentProps<HTMLTableSectionElement>) => (
      <tbody className="divide-y divide-grey-plain dark:divide-grey-plain-dark" {...props}>
        {nodeChildren}
      </tbody>
    ),
    // Images
    img: ({ ...props }: MarkdownComponentProps<HTMLImageElement>) => (
      <img className="max-w-full h-auto rounded-lg my-4" loading="lazy" {...props} />
    ),
    // Strikethrough (del)
    del: ({ children: nodeChildren, ...props }: MarkdownComponentProps<HTMLElement>) => (
      <del className="line-through text-grey-hover dark:text-grey-medium" {...props}>
        {nodeChildren}
      </del>
    ),
    // Input (checkbox for task lists)
    input: ({ ...props }: MarkdownComponentProps<HTMLInputElement>) => (
      <input
        className="mr-2 w-4 h-4 rounded border-grey-plain dark:border-grey-plain-dark"
        disabled
        {...props}
      />
    ),
  };

  return (
    <div className={`markdown-content ${className ?? ''}`}>
      {parsedContent.map((part, index) => {
        if (part.type === 'mermaid') {
          return <MermaidDiagram key={index} chart={part.content} />;
        }

        return (
          <ReactMarkdown
            key={index}
            remarkPlugins={[remarkGfm, remarkMath, remarkBreaks]}
            rehypePlugins={[rehypeKatex]}
            components={markdownComponents}
          >
            {part.content}
          </ReactMarkdown>
        );
      })}
    </div>
  );
};

export default Markdown;
